import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Host, Inject, Input, NgModule, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { WINDOW } from '@app/common/app-tokens';
import { BreakPoints } from '@app/common/breakpoints';
import { Utilities } from '@app/common/utilities';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { FeedItem } from '@app/models/feed-item';
import { FeedInfo } from '@app/models/feed-source-info';
import { LinkModule } from '@app/directives/link.directive';
import { FeedService } from '@app/services/feed.service';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, startWith, tap } from 'rxjs/operators';
import { RssFeedAmount } from '@app/common/rss-feed';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';

@Component({
  selector: 'app-rss-feeds',
  templateUrl: './rss-feeds.component.html',
  styleUrls: ['./rss-feeds.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RssFeedsComponent implements OnInit {

  @Input() maxNumberOfShowMore = 2; // How may times can you press the show more button
  @Input() titleMaxLength = 100;
  amountToFetch: number;
  amountToShowPerLoad: number;
  amountCurrentlyShowing: number;
  feedLoading: boolean;
  fetchedAmountOfFeeds: number;
  feedsFrom: Array<FeedInfo>;
  tagName: string;
  isFeedUrlConfigured: boolean;
  subscribedToRssFeature: boolean;
  feeds$: Observable<FeedItem[]>;
  private moreFeedsSubject = new Subject<number>();
  rssFeedConfig = this.featureSettingsStoreService.state.rssFeedConfig;

  constructor(private featureSettingsStoreService: FeatureSettingsStoreService,
              @Inject(WINDOW) private window: Window,
              private service: FeedService,
              @Host() @Optional() private hideParentIfEmptyDirective: HideParentIfEmptyDirective,
              private userLanguageTermService: UserLanguageTermService, ) { }

  ngOnInit() {

    if (this.rssFeedConfig) {

      // Vary amount of items to show based on screen size (and thus breakpoint).
      // Goal: 1) display even amount, and 2) show suitable amount for the resolution
      const smallModes = { [BreakPoints.xs]: 2, [BreakPoints.sm]: 2, [BreakPoints.md]: 4, [BreakPoints.lg]: 3, [BreakPoints.xl]: 3, [BreakPoints.xxl]: 4, [BreakPoints.xxxl]: 4 };
      const mediumModes = { [BreakPoints.xs]: 4, [BreakPoints.sm]: 4, [BreakPoints.md]: 8, [BreakPoints.lg]: 6, [BreakPoints.xl]: 6, [BreakPoints.xxl]: 8, [BreakPoints.xxxl]: 8 };
      const breakpoint = Utilities.getCurrentBootstrapBreakpoint(this.window);
      if (this.rssFeedConfig.amountToShowPerLoad === RssFeedAmount.Small) {
        this.amountToShowPerLoad = smallModes[breakpoint];
      } else { // Medium
        this.amountToShowPerLoad = mediumModes[breakpoint];
      }

      this.amountToFetch = this.amountToShowPerLoad * (this.maxNumberOfShowMore + 1);

      this.subscribedToRssFeature = true;
      this.feedsFrom = this.rssFeedConfig.feedLinksAndTagName;
      this.amountCurrentlyShowing = this.amountToShowPerLoad;

      if (this.feedsFrom.length > 0) {
        this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
        this.isFeedUrlConfigured = true;
        this.feeds$ = this.moreFeedsSubject
          .pipe(startWith(this.amountCurrentlyShowing))
          .pipe(concatMap((amount) => {
            this.amountCurrentlyShowing = amount;
            this.feedLoading = true;
            // filter feed links based on user interests to rss feed language setting in admin center
            return this.userLanguageTermService.getUserLanguageTerms()
              .pipe(
                concatMap((userChannelTerms) => {
                  const feedinfo = this.feedsFrom.filter(feeds => userChannelTerms.some(channelTerm => (channelTerm.LanguageTermId === feeds.Language)) || !feeds.Language || feeds.Language === '');
                  if (feedinfo.length > 0) {
                    return this.service.getRssFeeds(feedinfo, this.amountToFetch);
                  } else {
                    return of([]);
                  }
                })
              );
          }))
          .pipe(tap((feeds) => {
            if (feeds.length === 0) {
              this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
            } else {
              this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
              this.fetchedAmountOfFeeds = feeds.length;
              if (this.fetchedAmountOfFeeds > this.amountCurrentlyShowing) {
                feeds.splice(this.amountCurrentlyShowing, this.fetchedAmountOfFeeds);
              }
              this.feedLoading = false;
            }
          }));
      } else {
        this.isFeedUrlConfigured = false;
        this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
      }
    } else {
      this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
      this.subscribedToRssFeature = false;
    }
  }

  showMoreFeeds() {
    this.amountCurrentlyShowing = this.amountCurrentlyShowing + this.amountToShowPerLoad;
    this.moreFeedsSubject.next(this.amountCurrentlyShowing);
  }

  showLessFeeds() {
    this.amountCurrentlyShowing = this.amountToShowPerLoad;
    this.moreFeedsSubject.next(this.amountCurrentlyShowing);
  }
}

@NgModule({
  declarations: [RssFeedsComponent],
  exports: [RssFeedsComponent],
  imports: [
    CommonModule,
    MatButtonModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule,
    LinkModule,
  ]
})
export class RssFeedsModule { }
